import { useEffect, useRef, useState } from 'react';
import { useI18n } from './i18n';
import { isError, startSignIn, verifyCode } from './api';

/**
 * Sign in with a WhatsApp number and a code.
 *
 * Two steps on one screen rather than two screens, so the number stays visible
 * while the code is typed — the most common mistake is entering a code for a
 * number you mistyped, and being able to see it is what catches that.
 */
type Stage = 'number' | 'code' | 'done';

export default function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const { t } = useI18n();
  const [stage, setStage] = useState<Stage>('number');
  const [phone, setPhone] = useState('+506 ');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();
  const codeRef = useRef<HTMLInputElement>(null);

  // Focus follows the step, so a phone keyboard does not have to be dismissed
  // and the field re-tapped between entering a number and entering a code.
  useEffect(() => {
    if (stage === 'code') codeRef.current?.focus();
  }, [stage]);

  async function requestCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);

    const result = await startSignIn(phone);
    setBusy(false);

    if (isError(result)) {
      setProblem(
        result.error === 'invalid_number'
          ? t('signin.error.number')
          : result.error === 'rate_limited'
            ? t('signin.error.tooMany')
            : t('signin.error.generic'),
      );
      return;
    }
    setStage('code');
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);

    const result = await verifyCode(phone, code);
    setBusy(false);

    if (isError(result)) {
      setProblem(t('signin.error.code'));
      setCode('');
      codeRef.current?.focus();
      return;
    }
    setStage('done');
    onSignedIn();
  }

  if (stage === 'done') {
    return (
      <section className="panel">
        <h2>{t('signin.done.title')}</h2>
        <p>{t('signin.done.body')}</p>
      </section>
    );
  }

  return (
    <section className="panel">
      <h2>{t('signin.title')}</h2>

      <form onSubmit={stage === 'number' ? requestCode : submitCode} className="form">
        <label className="field">
          <span>{t('signin.number.label')}</span>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            name="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={stage !== 'number' || busy}
            required
          />
          <small>{t('signin.number.hint')}</small>
        </label>

        {stage === 'code' && (
          <label className="field">
            <span>{t('signin.code.label')}</span>
            <input
              ref={codeRef}
              type="text"
              // `one-time-code` is what makes a phone offer the code from the
              // notification instead of making somebody switch apps to read it.
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              name="code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
              disabled={busy}
              required
            />
            <small>{t('signin.code.hint')}</small>
          </label>
        )}

        {problem && (
          <p className="problem" role="alert">
            {problem}
          </p>
        )}

        <div className="actions">
          <button type="submit" className="button primary" disabled={busy}>
            {busy
              ? t('signin.working')
              : stage === 'number'
                ? t('signin.number.submit')
                : t('signin.code.submit')}
          </button>

          {stage === 'code' && (
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() => {
                setStage('number');
                setCode('');
                setProblem(undefined);
              }}
            >
              {t('signin.code.back')}
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
